import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

const SubscriptionSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifySubscription = async () => {
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        console.error('No session ID found');
        setVerifying(false);
        return;
      }

      // Give webhook a moment to process
      await new Promise(resolve => setTimeout(resolve, 2000));

      try {
        // Verify subscription status
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const response = await fetch(`${apiUrl}/subscription/status/${user?.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const data = await response.json();

        if (data.success && data.isSubscriber) {
          setSuccess(true);
          // Note: User will see updated status on next page load
        } else {
          console.log('Subscription not yet active, will redirect to dashboard');
        }
      } catch (error) {
        console.error('Failed to verify subscription:', error);
      } finally {
        setVerifying(false);
      }
    };

    verifySubscription();
  }, [searchParams, user]);

  const handleContinue = () => {
    navigate('/player-dashboard');
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-400 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('player.verifyingPayment')}</h2>
          <p className="text-blue-200">{t('player.pleaseWaitConfirm')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-xl p-8 border border-purple-400 max-w-md w-full">
        <div className="text-center">
          {success ? (
            <>
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold text-white mb-4">{t('player.welcomeToPremium')}</h2>
              <div className="bg-white/10 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <span className="text-4xl">👑</span>
                  <span className="text-2xl font-bold text-yellow-300">{t('player.premiumMember')}</span>
                </div>
                <p className="text-purple-200 text-sm">
                  {t('player.subscriptionActive')}
                </p>
              </div>
              <div className="text-left text-purple-200 mb-6 space-y-2">
                <p className="font-semibold text-white mb-2">{t('player.nowHaveAccess')}</p>
                <div className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>{t('player.accessBenefit1')}</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>{t('player.accessBenefit2')}</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>{t('player.accessBenefit3')}</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2">✅</span>
                  <span>{t('player.accessBenefit4')}</span>
                </div>
              </div>
              <button
                onClick={handleContinue}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-yellow-900 font-bold py-3 px-6 rounded-lg transition duration-200 transform hover:scale-105"
              >
                {t('player.continueToDashboard')}
              </button>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-3xl font-bold text-white mb-4">{t('player.paymentProcessing')}</h2>
              <p className="text-purple-200 mb-6">
                {t('player.paymentProcessingMessage')}
              </p>
              <button
                onClick={handleContinue}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('player.returnToDashboard')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
